import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

interface BreadcrumbContextValue {
  /** Title of the page currently rendered, used as the last crumb on detail routes. */
  leaf: string | null;
  setLeaf: (label: string | null) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue>({ leaf: null, setLeaf: () => {} });

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [leaf, setLeaf] = useState<string | null>(null);
  const value = useMemo(() => ({ leaf, setLeaf }), [leaf]);
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

export function useBreadcrumb() {
  return useContext(BreadcrumbContext);
}

/** Registers a page's title as the leaf crumb — called by PageHeader, so every page gets one. */
export function useRegisterCrumb(label: string | null | undefined) {
  const { setLeaf } = useBreadcrumb();
  useEffect(() => {
    setLeaf(label ?? null);
    return () => setLeaf(null);
  }, [label, setLeaf]);
}
